<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import type { MergedReportImage } from "@/types/report-images.types";
import VisualiseImage from "../findings/VisualiseImage.vue";
import ImageStatusBadge from "./ImageStatusBadge.vue";
import PhotoActionsDropdown from "./PhotoActionsDropdown.vue";

const props = defineProps<{
  image: MergedReportImage;
  diagnosticId: number;
  reportId: number;
  selected: boolean;
}>();

const emit = defineEmits<{
  (e: "toggle-selection", event: MouseEvent): void;
  (e: "drag-start", event: DragEvent): void;
  (e: "action-performed"): void;
  (e: "edit"): void;
}>();

const { t } = useTranslation();

// Display status: PLACED is treated as SORTED for display purposes
const status = computed(() => {
  // PLACED means has findings, but we show it as SORTED
  return props.image.status === "PLACED" ? "SORTED" : props.image.status;
});

// Findings count
const findingsCount = computed(() => props.image.findings?.length ?? 0);

// Is diagnostic overview (now called isOverview in ReportImage)
const isDiagnosticOverview = computed(() => props.image.isOverview);

// Is section overview (has overviewSectionId)
const isSectionOverview = computed(() => !!props.image.overviewSectionId);
</script>

<template>
  <div
    class="gallery-card bg-white rounded-3 shadow-sm overflow-hidden"
    :class="{
      selected: selected,
      ignored: image.excluded,
    }"
    :draggable="true"
    @dragstart="emit('drag-start', $event)"
  >
    <!-- Image container -->
    <div class="image-container position-relative">
      <VisualiseImage
        v-if="image.fileId"
        :id="image.fileId"
        class="gallery-image"
        fill
        previewMode="overlay"
      />
    </div>

    <!-- Zone inférieure : toute la zone ouvre la modal d'édition,
         sauf le bouton d'action (et l'image, qui est au-dessus). -->
    <div class="card-lower" @click="emit('edit')">
      <!-- Card footer with status and actions -->
      <div class="card-footer px-2 py-2">
        <div class="mb-1 justify-content-between d-flex align-items-center">
          <!-- Status badge -->
          <ImageStatusBadge :status="status" show-label size="sm" />
          <!-- Actions : ne déclenche pas l'édition -->
          <span @click.stop>
            <PhotoActionsDropdown
              :image="image"
              :diagnostic-id="diagnosticId"
              :report-id="reportId"
              @action-performed="emit('action-performed')"
            />
          </span>
        </div>

        <!-- Number + Description -->
        <div
          class="description-line small mb-1 text-truncate gap-2 d-flex flex-row rounded px-1"
        >
          <!-- Pas encore dans le rapport → n° identifiant ; sinon → n° rapport -->
          <span
            v-if="
              (image.isVirtual ? image.photoNumber : image.numbering) != null
            "
            class="text-muted"
            >#{{ image.isVirtual ? image.photoNumber : image.numbering }}</span
          >
          <span v-else class="no-description">#--</span>
          <span v-if="image.description" class="text-muted text-truncate">
            {{ image.description }}
          </span>
          <span v-else class="no-description text-truncate">
            {{ t("report:photos.noDescription") }}
          </span>
        </div>
      </div>

      <!-- Additional badges (findings, overview) -->
      <div
        v-if="findingsCount > 0 || isDiagnosticOverview || isSectionOverview"
        class="d-flex flex-wrap gap-1 mb-1 mx-2"
      >
        <!-- Findings badge -->
        <span v-if="findingsCount > 0" class="info-badge">
          <el-icon size="10"><i class="bi-link-45deg"></i></el-icon>
          {{ findingsCount }}
          {{
            findingsCount > 1
              ? t("report:photos.badge.finding_plural")
              : t("report:photos.badge.finding")
          }}
        </span>
        <!-- Diagnostic overview badge -->
        <span v-if="isDiagnosticOverview" class="info-badge">
          {{ t("report:photos.badge.diagnosticOverview") }}
        </span>
        <!-- Section overview badge -->
        <span v-if="isSectionOverview" class="info-badge">
          {{ t("report:photos.badge.sectionOverview") }}
        </span>
      </div>
    </div>

    <div
      class="item-header-actions d-flex justify-content-between align-items-center px-1 my-1"
    >
      <div class=" " @click="emit('toggle-selection', $event)">
        <div
          class="checkbox-indicator d-flex align-items-center justify-content-center cursor-pointer"
          :class="{ checked: selected }"
        >
          <el-icon v-if="selected" size="18" class="text-white">
            <i class="bi-check"></i>
          </el-icon>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/style/variables.scss";

.gallery-card {
  cursor: grab;
  transition: all 0.2s;
  border: 2px solid transparent;
  position: relative;
  min-width: 0;
  overflow: hidden;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  &.selected {
    border-color: $primary;
    box-shadow: 0 0 0 2px rgba($primary, 0.2);
  }

  &.ignored {
    opacity: 0.6;
  }
}

.card-lower {
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }
}

.item-header-actions {
  position: absolute;
  top: 4px;
  left: 4px;
  right: 4px;
  pointer-events: none;
  z-index: 2;

  > * {
    pointer-events: auto;
  }
}

.image-container {
  position: relative;
  overflow: hidden;
  aspect-ratio: 16/9;
}

.gallery-image {
  width: 100%;
  height: 100%;
}

.selection-checkbox {
  cursor: pointer;
  z-index: 2;
}

.checkbox-indicator {
  width: 20px;
  height: 20px;
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  transition: all 0.2s;

  &.checked {
    background-color: $primary;
    border-color: $primary;
  }
}

.card-footer {
  background-color: white;
  border-top: 1px solid var(--bs-border-color);
}

.info-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 500;
  background-color: var(--bs-dark);
  color: white;
  border-radius: 4px;
  white-space: nowrap;
}

.description-line {
  line-height: 1.4;
}

.clickable-description {
  cursor: pointer;
  transition: background-color 0.15s;
  margin-left: -4px;
  margin-right: -4px;

  &:hover {
    background-color: var(--bs-gray-200);
  }
}

.photo-number {
  font-weight: 600;
  color: var(--bs-body-color);
  margin-right: 4px;
}

.no-description {
  color: #9ca3af;
  font-style: italic;
}
</style>
